// frontend/src/context/EnterpriseAuthContext.jsx

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import authenticationService from '../services/authenticationService';

// Authentication context
const EnterpriseAuthContext = createContext();

// Action types
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  CLEAR_USER: 'CLEAR_USER',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_AUTH_METHOD: 'SET_AUTH_METHOD',
  SET_INITIAL_STATE: 'SET_INITIAL_STATE',
  UPDATE_MODE: 'UPDATE_MODE',
};

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  authMethod: 'none',
  error: null,
};

// Reducer
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        // Don't change isLoading here - let SET_LOADING handle it
        error: null,
      };

    case AUTH_ACTIONS.CLEAR_USER:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        authMethod: 'none',
      };

    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AUTH_ACTIONS.SET_AUTH_METHOD:
      return {
        ...state,
        authMethod: action.payload,
      };

    case AUTH_ACTIONS.SET_INITIAL_STATE:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: action.payload.authenticated,
        isLoading: false,
        authMethod: action.payload.method || 'none',
        error: null,
      };

    case AUTH_ACTIONS.UPDATE_MODE:
      return {
        ...state,
        user: {
          ...state.user,
          mode: action.payload.mode,
          facility_id: action.payload.facilityId,
        },
      };

    default:
      return state;
  }
}

/**
 * Enterprise Authentication Provider Component
 */
export function EnterpriseAuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize authentication on mount - but only once
  useEffect(() => {
    // Only initialize if we don't have auth state yet
    if (!state.user && state.isLoading) {
      initializeAuth();
    }
  }, []); // Empty deps - only run once on mount
  
  // Clean up navigation flag when location changes
  useEffect(() => {
    const path = window.location.pathname;
    // If we've navigated away from login pages, clear the justLoggedIn flag
    if (!path.startsWith('/login') && !path.startsWith('/asc/login')) {
      const justLoggedIn = sessionStorage.getItem('justLoggedIn');
      if (justLoggedIn) {
        const loginTime = parseInt(justLoggedIn);
        if (Date.now() - loginTime > 2000) {
          sessionStorage.removeItem('justLoggedIn');
        }
      }
    }
  }, []);

  // Set up authentication service listener
  useEffect(() => {
    const unsubscribe = authenticationService.addListener(handleAuthEvent);
    return unsubscribe;
  }, []);

  /**
   * Initialize authentication state
   */
  const initializeAuth = async () => {
    console.log('[EnterpriseAuthContext] initializeAuth called, current state:', {
      hasUser: !!state.user,
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading
    });
    const failSafeTimeoutMs = 5000;
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      console.warn('[EnterpriseAuthContext] Auth init timed out - falling back to unauthenticated state');
      dispatch({
        type: AUTH_ACTIONS.SET_INITIAL_STATE,
        payload: {
          user: null,
          authenticated: false,
          method: 'none',
        },
      });
    }, failSafeTimeoutMs);
    
    // CRITICAL: Don't re-check auth if we already have a user from login
    if (state.user && state.isAuthenticated) {
      console.log('[EnterpriseAuthContext] Already authenticated, skipping init');
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      clearTimeout(timeoutId);
      return;
    }

    // Check if we just logged in (within the last 10 seconds)
    const justLoggedIn = sessionStorage.getItem('justLoggedIn');
    console.log('[EnterpriseAuthContext] justLoggedIn flag:', justLoggedIn);
    
    if (justLoggedIn) {
      const loginTime = parseInt(justLoggedIn);
      const now = Date.now();
      if (now - loginTime < 10000) {
        console.log('[EnterpriseAuthContext] Just logged in, checking for stored user');
        // We just logged in, the user should already be set from login
        // But if not, try to get it from localStorage
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        console.log('[EnterpriseAuthContext] Stored user exists:', !!storedUser, 'Token exists:', !!token);
        
        if (storedUser && token) {
          try {
            const user = JSON.parse(storedUser);
            console.log('[EnterpriseAuthContext] Restoring user from localStorage after login:', user);
            dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
            dispatch({ type: AUTH_ACTIONS.SET_AUTH_METHOD, payload: 'jwt' });
          } catch (e) {
            console.error('[EnterpriseAuthContext] Failed to parse stored user');
          }
        }
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return;
      } else {
        // Old login flag, remove it
        console.log('[EnterpriseAuthContext] Old login flag, removing');
        sessionStorage.removeItem('justLoggedIn');
      }
    }

    // Skip auth check for public pages ONLY
    const publicPaths = [
      '/',
      '/login',
      '/asc/login',
      '/oauth/callback',
      // '/patient/login', - deprecated, using unified /login
      '/patient/register',
      '/patient/forgot-password',
      '/patient/reset-password',
      '/patient/verify-email',
    ];
    const currentPath = window.location.pathname;
    const isPublicPath = publicPaths.some(
      path =>
        currentPath === path ||
        currentPath.startsWith('/oauth/callback')
    );

    if (isPublicPath) {
      dispatch({
        type: AUTH_ACTIONS.SET_INITIAL_STATE,
        payload: {
          user: null,
          authenticated: false,
          method: 'none',
        },
      });
      clearTimeout(timeoutId);
      return;
    }

    try {
      console.log('[EnterpriseAuthContext] Calling checkAuthStatus...');
      const authStatus = (await authenticationService.checkAuthStatus()) || { authenticated: false, user: null, method: 'none' };
      try {
        console.log('[EnterpriseAuthContext] Auth status result:', {
          authenticated: !!authStatus?.authenticated,
          hasUser: !!authStatus?.user,
          method: authStatus?.method,
        });
      } catch {}

      dispatch({
        type: AUTH_ACTIONS.SET_INITIAL_STATE,
        payload: {
          user: authStatus.user,
          authenticated: authStatus.authenticated,
          method: authStatus.method,
        },
      });
      clearTimeout(timeoutId);
    } catch (error) {
      console.error('[EnterpriseAuthContext] Failed to initialize authentication:', error);
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      // Ensure isLoading is false and isAuthenticated is false on error
      dispatch({
        type: AUTH_ACTIONS.SET_INITIAL_STATE,
        payload: {
          user: null,
          authenticated: false,
          method: 'none',
        },
      });
      clearTimeout(timeoutId);
    }
  };

  /**
   * Handle authentication service events
   */
  const handleAuthEvent = event => {
    switch (event.type) {
      case 'login':
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: event.user });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_METHOD, payload: event.method });
        break;

      case 'logout':
        dispatch({ type: AUTH_ACTIONS.CLEAR_USER });
        break;

      case 'auth_failure':
        dispatch({ type: AUTH_ACTIONS.CLEAR_USER });
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: 'Authentication failed' });
        break;

      case 'userUpdate':
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: event.user });
        break;

      default:
        break;
    }
  };

  /**
   * Login function
   */
  const login = async (credentials, options = {}) => {
    let result;
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      result = await authenticationService.login(credentials, options);

      if (result.success) {
        console.log('[EnterpriseAuthContext] Login successful, user:', result.user);
        
        // Set user data immediately after successful login
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: result.user });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_METHOD, payload: result.method || 'jwt' });
        
        // Store login timestamp to prevent re-initialization
        sessionStorage.setItem('justLoggedIn', Date.now().toString());
        
        console.log('[EnterpriseAuthContext] State updated, justLoggedIn flag set');
        
        // CRITICAL: Set loading to false AFTER setting user
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        
        // Determine redirect path based on user type
        let redirectPath = '/dashboard';
        if (result.user) {
          // Check for patient user
          if (result.user.role === 'patient' || result.user.role === 'PATIENT' || 
              result.user.userType === 'patient') {
            redirectPath = '/patient/dashboard';
            console.log('[EnterpriseAuthContext] Patient user detected, redirecting to patient dashboard');
          } 
          // Check for ASC mode
          else if (result.user.mode === 'asc' || result.user.federation === 'asc') {
            redirectPath = '/asc/dashboard';
            console.log('[EnterpriseAuthContext] ASC user detected, redirecting to ASC dashboard');
          }
          // Use default path if provided
          else if (result.user.default_path) {
            redirectPath = result.user.default_path;
            console.log('[EnterpriseAuthContext] Using user default path:', redirectPath);
          }
          
          console.log('[EnterpriseAuthContext] User details for redirect:', {
            role: result.user.role,
            userType: result.user.userType,
            mode: result.user.mode,
            federation: result.user.federation,
            determinedPath: redirectPath
          });
        }

        console.log(`[EnterpriseAuthContext] Login success, redirect path: ${redirectPath}`);
        return { success: true, user: result.user, redirectPath };
      }
      const errorMessage = result.error?.message || (typeof result.error === 'object' ? JSON.stringify(result.error) : result.error) || 'Login failed';
      throw new Error(errorMessage);
    } catch (error) {
      console.error('[EnterpriseAuthContext] Login failed:', error.message, { originalError: error });
      const finalErrorMessage = error.message || 'An unknown error occurred during login.';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: finalErrorMessage });
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      return { success: false, error: finalErrorMessage };
    }
  };

  /**
   * Logout function
   */
  const logout = async () => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      await authenticationService.logout();
      dispatch({ type: AUTH_ACTIONS.CLEAR_USER });
    } catch (error) {
      console.error('[EnterpriseAuthContext] Logout failed:', error);
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      // Still clear the user even if the server logout fails
      dispatch({ type: AUTH_ACTIONS.CLEAR_USER });
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  /**
   * Refresh authentication
   */
  const refreshAuth = async () => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      const authStatus = await authenticationService.checkAuthStatus();
      if (authStatus.authenticated) {
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: authStatus.user });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_METHOD, payload: authStatus.method });
      } else {
        dispatch({ type: AUTH_ACTIONS.CLEAR_USER });
      }
    } catch (error) {
      console.error('[EnterpriseAuthContext] Failed to refresh authentication:', error);
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  /**
   * Clear error
   */
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  const hasRole = role => {
    return state.user && state.user.roles && state.user.roles.includes(role);
  };

  const hasPermission = permission => {
    return state.user && state.user.permissions && state.user.permissions.includes(permission);
  };

  /**
   * OAuth login functions
   */
  const initiateOAuthLogin = async (provider, options = {}) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const result = await authenticationService.initiateOAuthLogin(provider, options);
      return result;
    } catch (error) {
      const errorMessage = error.message || 'OAuth login failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      throw new Error(errorMessage);
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  const handleOAuthCallback = async (provider, params) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const result = await authenticationService.handleOAuthCallback(provider, params);

      if (result.success) {
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: result.user });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_METHOD, payload: 'oauth' });
        return { success: true };
      }
      throw new Error(result.error || 'OAuth callback failed');
    } catch (error) {
      const errorMessage = error.message || 'OAuth callback failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      throw new Error(errorMessage);
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  /**
   * Update user mode without reloading
   */
  const updateMode = (mode, facilityId) => {
    dispatch({
      type: AUTH_ACTIONS.UPDATE_MODE,
      payload: { mode, facilityId },
    });
  };

  /**
   * SAML login functions
   */
  const initiateSAMLLogin = async (provider, options = {}) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const result = await authenticationService.initiateSAMLLogin(provider, options);
      return result;
    } catch (error) {
      const errorMessage = error.message || 'SAML login failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      throw new Error(errorMessage);
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  const handleSAMLResponse = async (provider, samlResponse) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const result = await authenticationService.handleSAMLResponse(provider, samlResponse);

      if (result.success) {
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: result.user });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_METHOD, payload: 'saml' });
        return { success: true };
      }
      throw new Error(result.error || 'SAML response handling failed');
    } catch (error) {
      const errorMessage = error.message || 'SAML response handling failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      throw new Error(errorMessage);
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Failsafe: if auth loading hangs (e.g., after clearing site cache), fall back to unauthenticated
  useEffect(() => {
    if (!state.isLoading) return;
    const timer = setTimeout(() => {
      if (state.isLoading) {
        console.warn('[EnterpriseAuthContext] Force-clearing auth loading after timeout');
        dispatch({
          type: AUTH_ACTIONS.SET_INITIAL_STATE,
          payload: {
            user: null,
            authenticated: false,
            method: 'none',
          },
        });
      }
    }, 6000);
    return () => clearTimeout(timer);
  }, [state.isLoading]);

  // Context value
  const contextValue = {
    // State
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    authMethod: state.authMethod,
    error: state.error,

    // Federation mode - extracted from user object
    federationMode: state.user?.federation || state.user?.mode || null,

    // Actions
    login,
    logout,
    refreshAuth,
    clearError,
    updateMode,

    // Utility functions
    hasRole,
    hasPermission,

    // OAuth functions
    initiateOAuthLogin,
    handleOAuthCallback,

    // SAML functions
    initiateSAMLLogin,
    handleSAMLResponse,
  };

  return (
    <EnterpriseAuthContext.Provider value={contextValue}>{children}</EnterpriseAuthContext.Provider>
  );
}

/**
 * Hook to use enterprise authentication context
 */
export function useEnterpriseAuth() {
  const context = useContext(EnterpriseAuthContext);

  if (context === undefined) {
    throw new Error('useEnterpriseAuth must be used within an EnterpriseAuthProvider');
  }

  return context;
}

/**
 * HOC for components that require authentication
 */
export function withEnterpriseAuth(Component, options = {}) {
  const { redirectTo = '/login', requiredRole = null, requiredPermission = null } = options;

  return function AuthenticatedComponent(props) {
    const { isAuthenticated, isLoading, user, hasRole, hasPermission } = useEnterpriseAuth();

    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        // Redirect to login page
        window.location.href = redirectTo;
      } else if (!isLoading && isAuthenticated) {
        // Check role and permission requirements
        if (requiredRole && !hasRole(requiredRole)) {
          throw new Error(`Required role: ${requiredRole}`);
        }

        if (requiredPermission && !hasPermission(requiredPermission)) {
          throw new Error(`Required permission: ${requiredPermission}`);
        }
      }
    }, [isAuthenticated, isLoading, user, requiredRole, requiredPermission]); // Added dependencies to ensure effect re-runs on changes

    if (isLoading) {
      return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
      return null; // Will redirect
    }

    return <Component {...props} />;
  };
}

export default EnterpriseAuthContext;
